import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchBlogs } from '../../store/slices/blogSlice';

const BlogPage = () => {
  const dispatch = useAppDispatch();
  const { blogs, loading, pagination } = useAppSelector((state) => state.blog);

  const fetchBlogsData = (page = 1) => {
    dispatch(fetchBlogs({ page, limit: 9 }));
  };

  useEffect(() => {
    fetchBlogsData();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-jet-gray-100 pt-20 pb-10 mt-10">
      <div className="w-[86%] mx-auto">
        <h1 className="text-3xl font-semibold text-jet-gray-800 text-center mb-12">Our Blog</h1>

        {/* Loading/Empty State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow"></div>
          </div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-20 text-jet-gray-500">
            No blog posts found.
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-7 mb-5">
              {blogs.map((post, index) => (
                <article key={post.id ?? post.page_url ?? index} className="bg-white rounded-[10px] p-5 overflow-hidden shadow-jet flex flex-col h-full">
                  <div className="w-full h-50 overflow-hidden bg-gray-200">
                    {post.image_url ? (
                      <img
                        src={`${post.image_url}`}
                        alt={post.title}
                        className="w-full h-full object-contain transition-transform duration-300 ease-in-out hover:scale-105"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/hero-image.jpeg';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-jet-gray-400">
                        No Image
                      </div>
                    )}
                  </div>

                  <div className="p-6 flex flex-col flex-1">
                    <h2 className="text-lg font-semibold text-jet-gray-800 mb-4 leading-snug line-clamp-2">{post.title}</h2>
                    <p className="text-sm text-jet-gray-600 leading-relaxed mb-5 line-clamp-3">{post.summary || 'Click read more to see the full article.'}</p>

                    <div className="mt-auto pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-3 text-sm text-jet-gray-600 mb-3">
                        <span className="flex items-center gap-1.5">
                          <i className="fas fa-user text-[#ffd200]"></i>
                          <span>{post.created_by || 'Jamie C'}</span>
                        </span>
                        <span className="flex items-center gap-1.5">
                          <i className="fas fa-calendar text-[#ffd200]"></i>
                          <span>{formatDate(post.created_at)}</span>
                        </span>
                      </div>
                      
                      <Link 
                        to={`/blogs/${post.page_url}`}
                        className="text-[#ffd200] hover:text-[#e6bd00] font-bold text-l flex items-center gap-2 transition-colors no-underline"
                      >
                        Read More <FontAwesomeIcon icon={faArrowRight} className="text-xl" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                {[...Array(pagination.total_pages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => fetchBlogsData(i + 1)}
                    className={`px-4 py-2 rounded-md ${pagination.current_page === i + 1 ? 'bg-jet-yellow text-jet-gray-800' : 'bg-white text-jet-gray-600 border border-gray-200'}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogPage;